import {Component} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {
    CreationMethod,
    OrganizationType,
    ReferenceBookResponse,
    ReferenceBookType,
    SubjectApiService,
    SubjectCategoriesResponse,
    SubjectCategory,
    SubjectCategoryCreationReason,
    SubjectType,
} from '@npck/af-frontend-api';
import {DATE_TIME_SERVER_FORMAT, RouterService, rxSpinner, TAB} from '@npck/libs/commons';
import {combineLatest, Subscription} from 'rxjs';
import {
    fieldExtractor,
    FilterDictionaryField,
    FilterEvent,
    FilterField,
    FilterSelectField,
    getFormattedDateOrNull,
} from '../../components/full-table/filter/filter.component';
import {TableEvent} from '../../components/full-table/full-table.component';
import {Column, ColumnType, Row, StatusColumn} from '../../components/full-table/table/table.component';
import {StatusStyle} from '../../components/status/status.component';
import {AfCentreFilterHolderService} from '../../domain/af-centre-filter-holder.service';
import {AuthHolderService} from '../../domain/auth-holder.service';
import {DictionaryHolderService, findValueInDictionary} from '../../domain/dictionary-holder.service';
import {Path} from '../../path';

@Component({
    selector: 'af-subjects',
    templateUrl: './af-centre.component.html',
    standalone: false,
})
export class AfCentreComponent {
    tabs: AfCentreTab[] = [];
    activeTab: AfCentreTab;
    selectedTabIndex: number;
    showCreateButton: boolean = true;
    isNpckAdmin: boolean = false;
    dataSource: Row[] = [];

    filterFields: FilterField[] = [];

    tabResetKey!: Record<AfCentreTab, number>;
    private subjectsSubscription: Subscription;

    constructor(
        private routerService: RouterService,
        private activatedRoute: ActivatedRoute,
        private subjectApiService: SubjectApiService,
        private authHolderService: AuthHolderService,
        public filterHolderService: AfCentreFilterHolderService,
        private dictionaryHolderService: DictionaryHolderService,
    ) {
        const hasAccessToVictim = this.authHolderService.hasAccess(['victim-viewer']);
        const hasAccessToDropper = this.authHolderService.hasAccess(['npck-admin', 'government-user', 'finance-user']);
        const hasAccessToPotentialVictim = this.authHolderService.hasAccess(['npck-admin', 'potential-victim-manager']);
        const availableTabs: AfCentreTab[] = [];
        if (hasAccessToVictim) {
            availableTabs.push('tabVictim');
        }
        if (hasAccessToDropper) {
            availableTabs.push('tabDropper');
        }
        if (hasAccessToPotentialVictim) {
            availableTabs.push('tabPotentialVictim');
        }
        if (this.authHolderService.hasAccess(['npck-admin'])) {
            availableTabs.push('tabWhiteList');
        }
        this.tabs = availableTabs;
        const tabFromUrl = this.activatedRoute.snapshot.queryParamMap.get(TAB) as AfCentreTab | null;
        this.activeTab = (tabFromUrl && this.tabs.includes(tabFromUrl)) ? tabFromUrl : this.tabs[0];
        this.selectedTabIndex = this.tabs.indexOf(this.activeTab);
        this.tabResetKey = this.tabs.reduce((acc, tab) => {
            acc[tab] = 0;
            return acc;
        }, {} as Record<AfCentreTab, number>);
        this.handleNavigation(this.selectedTabIndex);
        this.isNpckAdmin = this.authHolderService.hasAccess(['npck-admin']);
        this.showCreateButton = this.authHolderService.hasAccess(['npck-admin', 'potential-victim-manager']);
    }

    handleNavigation($event: number): void {
        this.activeTab = this.tabs[$event];
        this.routerService.navigate(
            Path.AF_CENTRE,
            {
                queryParams: {[TAB]: this.activeTab},
                queryParamsHandling: 'merge',
            });
        this.filterFields = [
            {
                type: 'text',
                name: 'query',
            },
            <FilterSelectField<SubjectType>>{
                isMultiSelect: true,
                type: 'select',
                name: 'subjectType',
                values: Object.values(SubjectType),
            },
            <FilterSelectField<string>>{
                isMultiSelect: true,
                type: 'select',
                name: 'status',
                values: ['deleted', 'active'],
            },
            <FilterField>{
                type: 'dateRange',
                name: 'dateRange',
            },
            <FilterField>{
                type: 'dateRange',
                name: 'deletedDateRange',
            },
            <FilterSelectField<CreationMethod>>{
                isMultiSelect: true,
                type: 'select',
                name: 'creationMethod',
                values: Object.values(CreationMethod),
            },
            <FilterDictionaryField>{
                isMultiSelect: true,
                type: 'select',
                name: 'sourceOrganizations',
                dictionaryName: 'bin',
                values: [],
            },
            <FilterSelectField<OrganizationType>>{
                isMultiSelect: true,
                type: 'select',
                name: 'organizationType',
                values: Object.values(OrganizationType),
            },
            ...(this.activeTab === 'tabPotentialVictim' ? [
                <FilterSelectField<SubjectCategoryCreationReason>>{
                    isMultiSelect: true,
                    type: 'select',
                    name: 'creationReason',
                    values: Object.values(SubjectCategoryCreationReason),
                },
            ] : []),
        ];
        const state = this.filterHolderService.getState(this.activeTab);
        this.dataSource = state.dataSource ?? [];
    }

    navigateToCreateSubject(): void {
        this.routerService.navigateToTab<AfCentreTab>(Path.SUBJECTS_CREATE, this.activeTab);
    }

    restartActiveTab(): void {
        this.subjectsSubscription?.unsubscribe();
        this.filterHolderService.storeTableState(this.activeTab, {
            ...this.filterHolderService.getState(this.activeTab).tableEvent,
            filter: {},
        });
        this.filterHolderService.storeResponse(this.activeTab, null, []);
        this.dataSource = [];
        this.tabResetKey[this.activeTab] = (this.tabResetKey[this.activeTab] ?? 0) + 1;
    }

    loadSubject(tableEvent: TableEvent): void {
        this.subjectsSubscription?.unsubscribe();
        if (this.isFilterEmpty(tableEvent.filter)) {
            this.dataSource = [];
            this.filterHolderService.storeTableState(this.activeTab, {
                ...tableEvent,
                filter: {},
            });
            this.filterHolderService.storeResponse(this.activeTab, null, []);
            return;
        }
        if (this.handleCachedOrRestart(tableEvent)) {
            return;
        }

        const activeTabState = this.filterHolderService.getState(this.activeTab);
        const selectedCategories = tableEvent.filter?.category && fieldExtractor(tableEvent.filter?.category) as SubjectCategory[];
        const defaultCategory = this.filterHolderService.getDefaultFilterForTab(this.activeTab).category;
        const defaultCategories = fieldExtractor(defaultCategory) as SubjectCategory[];
        const isAllSelectedCategoriesAvailableToFilter =
            selectedCategories?.length && selectedCategories.every(selected => defaultCategories.includes(selected));
        const categoryToFilter = isAllSelectedCategoriesAvailableToFilter ? tableEvent.filter?.category : defaultCategory;

        this.filterHolderService.storeTableState(this.activeTab, {
            ...tableEvent,
            filter: {
                ...tableEvent.filter,
                category: categoryToFilter,
            },
        });
        this.dataSource = [];
        if (!activeTabState.response || activeTabState.tableEvent !== tableEvent) {
            const updatedState = this.filterHolderService.getState(this.activeTab);
            const filterEvent = updatedState.tableEvent.filter;
            const sortEvent = updatedState.tableEvent.sortBy;
            const pageEvent = updatedState.tableEvent.page;
            this.subjectsSubscription = combineLatest([
                this.dictionaryHolderService.getReference(ReferenceBookType.BIN),
                this.subjectApiService.getSubjectCategories({
                    limit: pageEvent.pageSize,
                    offset: pageEvent.pageOffset,
                    query: filterEvent.query,
                    isDeleted: this.extractIsDeleted(filterEvent),
                    sourceOrganizations: fieldExtractor(filterEvent?.sourceOrganizations),
                    organizationTypes: fieldExtractor(filterEvent?.organizationType) as OrganizationType[],
                    category: fieldExtractor(filterEvent?.category),
                    subjectTypes: fieldExtractor(filterEvent?.subjectType) as SubjectType[],
                    creationMethods: fieldExtractor(filterEvent?.creationMethod) as CreationMethod[],
                    creationReasons: fieldExtractor(filterEvent?.creationReason) as SubjectCategoryCreationReason[],
                    dateFrom: getFormattedDateOrNull(filterEvent?.dateRange?.dateFrom, DATE_TIME_SERVER_FORMAT),
                    dateTo: getFormattedDateOrNull(filterEvent?.dateRange?.dateTo, DATE_TIME_SERVER_FORMAT),
                    deletedDateFrom: getFormattedDateOrNull(filterEvent?.deletedDateRange?.dateFrom, DATE_TIME_SERVER_FORMAT),
                    deletedDateTo: getFormattedDateOrNull(filterEvent?.deletedDateRange?.dateTo, DATE_TIME_SERVER_FORMAT),
                    hasDeletedCategory: false,
                    shouldHideVictim: false,
                    sortBy: sortEvent?.columnName,
                    sortOrder: sortEvent?.direction,
                }),
            ])
                .pipe(rxSpinner(tableEvent.spinnerId))
                .subscribe(([organizations, response]) => {
                    this.showCreateButton = !this.authHolderService.hasAccess(['government-user', 'finance-user'])
                        || this.authHolderService.hasAccess(['potential-victim-manager']);
                    this.buildDataSource(response, organizations);
                    this.filterHolderService.storeResponse(this.activeTab, response, this.dataSource);
                });
        } else {
            this.subjectsSubscription = this.dictionaryHolderService.getReference(ReferenceBookType.BIN).subscribe(organizations =>
                this.buildDataSource(activeTabState.response, organizations),
            );
        }
    }

    private handleCachedOrRestart(tableEvent: TableEvent): boolean {
        const activeTabState = this.filterHolderService.getState(this.activeTab);
        if (tableEvent.spinnerId === 'initial' && activeTabState.response) {
            this.dataSource = activeTabState.dataSource ?? [];
            return true;
        }
        if (tableEvent.spinnerId === 'filter'
            && activeTabState.response
            && this.isFilterEmpty(tableEvent.filter)
        ) {
            this.restartActiveTab();
            return true;
        }
        return false;
    }

    private extractIsDeleted(filterEvent: FilterEvent): boolean | null {
        const selectedStatuses = fieldExtractor(filterEvent?.status);
        if (!selectedStatuses?.length || selectedStatuses.length === 2) {
            return null;
        }
        return selectedStatuses[0] === 'deleted';
    }

    private buildDataSource(response: SubjectCategoriesResponse, organizations?: ReferenceBookResponse[]): void {
        const isPotentialVictim = this.activeTab === 'tabPotentialVictim';
        this.dataSource = response.items.map(item => {
            const organizationDisplay = organizations
                ? (findValueInDictionary(organizations, item.createdByOrganization, 'name_ru') || item.createdByOrganization)
                : item.createdByOrganization;
            return <Row>{
                click: () => this.openFraudDetails(item.subjectId),
                is_deleted_category: <StatusColumn>{
                    type: ColumnType.STATUS,
                    sortable: true,
                    text: item.isDeleted ? 'i18n_af_statusDeleted' : 'i18n_af_statusActive',
                    style: item.isDeleted ? StatusStyle.ERROR : StatusStyle.SUCCESS,
                },
                categories: <Column>{
                    type: ColumnType.TEXT,
                    text: `i18n_af_category#${item.category}`,
                },
                subjectType: <Column>{
                    type: ColumnType.TEXT,
                    text: `i18n_af_subjectType#${item.subjectType}`,
                },
                identification_number: <Column>{
                    type: ColumnType.TEXT,
                    text: item.identificationNumber,
                },
                name: <Column>{
                    type: ColumnType.TEXT,
                    text: item.name,
                },
                patronymic: <Column>{
                    type: ColumnType.TEXT,
                    text: item.patronymic,
                },
                surname: <Column>{
                    type: ColumnType.TEXT,
                    text: item.surname,
                },
                created_date: <Column>{
                    type: ColumnType.DATE_TIME_SECONDS,
                    sortable: true,
                    text: item.createdDate,
                },
                created_by: <Column>{
                    type: ColumnType.TEXT,
                    text: item.createdBy,
                },
                updated_date: <Column>{
                    type: ColumnType.DATE_TIME_SECONDS,
                    sortable: true,
                    text: item.updatedDate,
                },
                updated_by: <Column>{
                    type: ColumnType.TEXT,
                    text: item.updatedBy,
                },
                deleted_date: <Column>{
                    type: ColumnType.DATE_TIME_SECONDS,
                    sortable: true,
                    text: item.deletedDate,
                },
                deleted_by: <Column>{
                    type: ColumnType.TEXT,
                    text: item.deletedBy,
                },
                creation_method: <Column>{
                    type: ColumnType.TEXT,
                    text: `i18n_af_filter_field_creationMethod#${item.creationMethod}`,
                },
                source_organization: <Column>{
                    type: ColumnType.TEXT,
                    text: organizationDisplay,
                },
                ...(isPotentialVictim && {
                    creation_reason: <Column>{
                        type: ColumnType.TEXT,
                        text: item.creationReason ? `i18n_af_filter_field_creationReason#${item.creationReason}` : '',
                    },
                }),
                id: <Column>{
                    type: ColumnType.TEXT,
                    text: item.subjectId,
                },
            };
        });
    }

    private openFraudDetails(id: string): void {
        this.routerService.navigateToTab<AfCentreTab>(`${Path.SUBJECTS}/${id}`, this.activeTab);
    }

    private isFilterEmpty(filter?: FilterEvent): boolean {
        if (!filter) {
            return true;
        }
        const filterRecord: Record<string, unknown> = filter;
        const query = filterRecord.query;
        if (typeof query === 'string' && query.trim().length > 0) {
            return false;
        }
        const names = this.filterFields
            .map(f => f.name)
            .filter(name => name !== 'category');
        return !names.some(name => {
            const value = filterRecord[name];
            if (!value) {
                return false;
            }
            if (typeof value === 'object') {
                return Object.values(value).some(Boolean);
            }
            return false;
        });
    }
}

export const AF_CENTRE_TABS = ['tabVictim', 'tabDropper', 'tabPotentialVictim', 'tabWhiteList'];
export type AfCentreTab = typeof AF_CENTRE_TABS[number];
